/**
 * Qobuz Playlist Import — frontend driver
 *
 * Runs the batched POST /api/user/playlists/import/qobuz loop until the
 * backend reports moreAvailable=false, then dispatches a 'maestro:playlists-updated'
 * event so MyPlaylistsPage refreshes without needing a reload.
 *
 * Concurrency: at most one run per tab, serialized via a promise.
 * Platforms: web, iOS WebView, desktop Tauri — all hit the same Cloudflare API.
 */

import { CLOUD_API_URL } from '../config/serverConfig';

type ImportMode = 'initial' | 'refresh';

export interface ImportProgress {
  imported: number;        // new playlists added this run
  updated: number;         // existing playlists refreshed this run
  skippedUserEdited: number;
  playlistsProcessed: number;
  totalFound: number;
  done: boolean;
  error?: string;
}

export interface ImportStatus {
  lastRunAt: string | null;
  lastInitialImportAt: string | null;
  totalImported: number;
  totalUpdated: number;
  totalSkippedUserEdited: number;
}

// localStorage flag so we don't auto-import on every app load.
const INITIAL_IMPORT_FLAG = 'maestro_qobuz_playlists_imported_v4';

let runningPromise: Promise<ImportProgress> | null = null;

function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem('maestro_auth');
    return stored ? JSON.parse(stored).accessToken : null;
  } catch {
    return null;
  }
}

function authedFetch(path: string, method: string, body?: unknown): Promise<Response> {
  const token = getAuthToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  return fetch(`${CLOUD_API_URL}${path}`, init);
}

export function hasCompletedInitialQobuzImport(): boolean {
  try {
    return localStorage.getItem(INITIAL_IMPORT_FLAG) === '1';
  } catch {
    return false;
  }
}

function markInitialQobuzImportDone(): void {
  try {
    localStorage.setItem(INITIAL_IMPORT_FLAG, '1');
  } catch {}
}

export function clearQobuzImportFlag(): void {
  try {
    localStorage.removeItem(INITIAL_IMPORT_FLAG);
  } catch {}
}

/** Fetch the backend's stored import status (last run, totals). */
export async function fetchQobuzImportStatus(): Promise<ImportStatus | null> {
  try {
    const res = await authedFetch('/api/user/playlists/import/qobuz/status', 'GET');
    if (!res.ok) return null;
    const data: any = await res.json();
    return data?.status ?? null;
  } catch {
    return null;
  }
}

interface RunOptions {
  mode?: ImportMode;
  onProgress?: (p: ImportProgress) => void;
}

/**
 * Run the import loop end-to-end. Safe to call concurrently — the second call
 * returns the in-flight promise instead of starting a new loop.
 */
export function runQobuzPlaylistImport(options: RunOptions = {}): Promise<ImportProgress> {
  if (runningPromise) return runningPromise;
  runningPromise = (async () => {
    const mode: ImportMode = options.mode ?? 'initial';
    let offset = 0;
    let imported = 0;
    let updated = 0;
    let skippedUserEdited = 0;
    let totalFound = 0;
    const maxIterations = 60; // safety cap; at batch=25 that's 1500 playlists

    for (let i = 0; i < maxIterations; i++) {
      let res: Response;
      try {
        res = await authedFetch('/api/user/playlists/import/qobuz', 'POST', { offset, mode });
      } catch (err: any) {
        const progress: ImportProgress = {
          imported, updated, skippedUserEdited,
          playlistsProcessed: offset, totalFound,
          done: true,
          error: err?.message || 'Network error during import',
        };
        options.onProgress?.(progress);
        return progress;
      }

      if (!res.ok) {
        let errMsg = `Import failed (HTTP ${res.status})`;
        let code: string | undefined;
        try {
          const body: any = await res.json();
          errMsg = body?.error || errMsg;
          code = body?.code;
        } catch {}
        const progress: ImportProgress = {
          imported, updated, skippedUserEdited,
          playlistsProcessed: offset, totalFound,
          done: true,
          error: code === 'qobuz_not_connected' ? 'Qobuz not connected' : errMsg,
        };
        options.onProgress?.(progress);
        return progress;
      }

      const data: any = await res.json();
      imported += data.batchImported ?? 0;
      updated += data.batchUpdated ?? 0;
      skippedUserEdited += data.batchSkippedUserEdited ?? 0;
      totalFound = data.totalFound ?? totalFound;
      offset = data.nextOffset ?? offset;

      const progress: ImportProgress = {
        imported, updated, skippedUserEdited,
        playlistsProcessed: offset,
        totalFound,
        done: !data.moreAvailable,
      };
      options.onProgress?.(progress);

      if (!data.moreAvailable) {
        markInitialQobuzImportDone();
        // Nudge the UI — MyPlaylistsPage and the sidebar can refetch.
        try {
          window.dispatchEvent(new CustomEvent('maestro:playlists-updated', {
            detail: { source: 'qobuz-import', ...progress },
          }));
        } catch {}
        return progress;
      }
    }

    // Hit safety cap.
    const progress: ImportProgress = {
      imported, updated, skippedUserEdited,
      playlistsProcessed: offset, totalFound,
      done: true,
      error: 'Import cap reached — some playlists may be missing',
    };
    options.onProgress?.(progress);
    return progress;
  })().finally(() => {
    runningPromise = null;
  });

  return runningPromise;
}

/**
 * Kick off an initial import after Qobuz connection, but only if we've never
 * completed one. Swallows all errors — this is a best-effort background task.
 */
export function maybeAutoImportQobuzPlaylists(): void {
  if (hasCompletedInitialQobuzImport()) return;
  if (runningPromise) return;
  const token = getAuthToken();
  if (!token) return;
  runQobuzPlaylistImport({ mode: 'initial' }).catch(() => {});
}
